import { Button, Checkbox, DropdownItem, Form, FormField, Input, SelectA11y } from '@base-app';
import { CardGrid, Direction, LeavingPageModal } from '@builder';
import { yupResolver } from '@hookform/resolvers/yup';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useBlocker } from 'react-router-dom';
import { object, string } from 'yup';
import {
  StyledFormFieldsContainer,
  StyledFormSectionContainer,
  cardGridStyles,
  consentLabelStyles,
  inputNumberStyles,
  leftFormFieldStyles,
  rightFormFieldStyles,
  submitButtonStyles,
} from './CommunityOutreachForm.styles';
import { Sizes, Variants } from '@/common/types';
import { useTranslation } from 'react-i18next';
import { COMMUNITY_OUTREACH_TRANSLATION } from '../../constants/common';
import { useUserFieldsValidation } from '@/modules/shared/hooks/useUserFieldsValidation';
import { CommunityOutreachFormProps, CommunityOutreachFormValues } from './CommunityOutreachForm.types';
import { COMMON_TRANSLATION } from '@/common/constants';
import { languageOptions } from '../../constants/languages';
import { DATE_FORMAT_VALIDATION_REGEX, maskDate, maskPhoneNumber } from '@/common/utils';
import { getRaceOptions } from './CommunityOutreachForm.helper';

const CommunityOutreachForm: FC<CommunityOutreachFormProps> = ({ handleFormSubmit, isLoading, locations }) => {
  const { t } = useTranslation(COMMUNITY_OUTREACH_TRANSLATION);
  const { firstName, lastName, email: emailAddress, phoneNumber } = useUserFieldsValidation();
  const [consentCheck, setConsentCheck] = useState(false);
  const [locationOptions, setLocationOptions] = useState<DropdownItem[]>([]);

  const raceOptions = getRaceOptions(t);

  useEffect(() => {
    if (locations) {
      setLocationOptions(() =>
        locations.map((location) => ({
          id: location.locationName,
          label: location.locationName,
        })),
      );
    }
  }, [locations]);

  const schema = object({
    emailAddress,
    firstName,
    lastName,
    phoneNumber,
    dob: string()
      .required(
        t('form.errors.required', {
          ns: COMMON_TRANSLATION,
          label: t('form.date_of_birth'),
        }),
      )
      .trim()
      .matches(DATE_FORMAT_VALIDATION_REGEX, t('form.errors.date_of_birth')),
    race: string()
      .optional()
      .oneOf(raceOptions.map((race) => race.id)),
    preferredLanguage: string()
      .optional()
      .oneOf(languageOptions.map((language) => language.id)),
    mrn: string()
      .required(
        t('form.errors.required', {
          ns: COMMON_TRANSLATION,
          label: t('form.mrn'),
        }),
      )
      .max(
        50,
        t('form.errors.max_length', {
          ns: COMMON_TRANSLATION,
          label: t('form.mrn'),
          max: 50,
        }),
      ),
    outreachLocations: string()
      .required()
      .oneOf(locationOptions.map((location) => location.label)),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    setValue,
  } = useForm<CommunityOutreachFormValues>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const handleSubmitButtonClick: SubmitHandler<CommunityOutreachFormValues> = (values): void => {
    handleFormSubmit({
      ...values,
      phoneNumber: values.phoneNumber.replace(/[\s\-()]/g, ''),
    });
  };

  const blocker = useBlocker(isDirty);
  const isBlocked = blocker.state === 'blocked';

  useEffect(() => {
    if (isBlocked && !isDirty) {
      blocker.reset?.();
    }
  }, [blocker, isDirty]);

  return (
    <>
      <Form onSubmit={handleSubmit(handleSubmitButtonClick)}>
        <CardGrid css={cardGridStyles} direction={Direction.ROW} title={t('personal_details')}>
          <StyledFormSectionContainer>
            <StyledFormFieldsContainer>
              <FormField css={leftFormFieldStyles}>
                <Controller
                  control={control}
                  name="firstName"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!(errors.firstName == null)}
                      errorDescription={errors.firstName?.message as string}
                      fullWidth
                      id={field.name}
                      label={t('form.first_name')}
                      name={field.name}
                      placeholder={t('form.first_name_example')}
                      testId="community-outreach-form-input-first-name"
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
              <FormField css={rightFormFieldStyles}>
                <Controller
                  control={control}
                  name="lastName"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!(errors.lastName == null)}
                      errorDescription={errors.lastName?.message as string}
                      fullWidth
                      id={field.name}
                      label={t('form.last_name')}
                      name={field.name}
                      placeholder={t('form.last_name_example')}
                      testId="community-outreach-form-input-last-name"
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
            </StyledFormFieldsContainer>
            <StyledFormFieldsContainer>
              <FormField css={leftFormFieldStyles}>
                <Controller
                  control={control}
                  name="dob"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!(errors.dob == null)}
                      errorDescription={errors.dob?.message as string}
                      fullWidth
                      id={field.name}
                      label={t('form.date_of_birth')}
                      name={field.name}
                      placeholder={t('form.date_of_birth_example')}
                      testId="community-outreach-form-input-date-of-birth"
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setValue('dob', maskDate(event.target.value));
                      }}
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
              <FormField css={rightFormFieldStyles}>
                <Controller
                  control={control}
                  name="race"
                  render={({ field }) => {
                    const item = field.value as unknown as string;
                    return (
                      <SelectA11y
                        {...field}
                        defaultValue={item || ''}
                        error={!(errors.race == null)}
                        errorDescription={errors.race?.message as string}
                        fullWidth
                        id={field.name}
                        items={raceOptions}
                        label={t('form.race')}
                        listWidth="100%"
                        name={field.name}
                        placeholder={t('form.select_race')}
                        testId="community-outreach-form-input-race"
                        value={item || ''}
                        variant={Variants.PRIMARY}
                        isSensitive={true}
                      />
                    );
                  }}
                />
              </FormField>
            </StyledFormFieldsContainer>
            <StyledFormFieldsContainer>
              <FormField css={leftFormFieldStyles}>
                <Controller
                  control={control}
                  name="mrn"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!(errors.mrn == null)}
                      errorDescription={errors.mrn?.message as string}
                      fullWidth
                      id={field.name}
                      label={t('form.mrn')}
                      name={field.name}
                      placeholder={t('form.mrn_example')}
                      testId="community-outreach-form-input-mrn"
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
              <FormField css={rightFormFieldStyles}>
                <Controller
                  control={control}
                  name="preferredLanguage"
                  render={({ field }) => {
                    const item = field.value as unknown as string;
                    return (
                      <SelectA11y
                        {...field}
                        defaultValue={item || ''}
                        error={!(errors.preferredLanguage == null)}
                        errorDescription={errors.preferredLanguage?.message as string}
                        fullWidth
                        id={field.name}
                        items={languageOptions}
                        label={t('form.preferred_language')}
                        listWidth="100%"
                        name={field.name}
                        placeholder={t('form.select_preferred_language')}
                        testId="community-outreach-form-input-preferred-language"
                        value={item || ''}
                        variant={Variants.PRIMARY}
                      />
                    );
                  }}
                />
              </FormField>
            </StyledFormFieldsContainer>
            <StyledFormFieldsContainer>
              <FormField css={leftFormFieldStyles}>
                <Controller
                  control={control}
                  name="outreachLocations"
                  render={({ field }) => {
                    const item = field.value as unknown as string;
                    return (
                      <SelectA11y
                        {...field}
                        defaultValue={item || ''}
                        error={!(errors.outreachLocations == null)}
                        errorDescription={errors.outreachLocations?.message as string}
                        fullWidth
                        id={field.name}
                        items={locationOptions}
                        label={t('form.outreach_location')}
                        listWidth="100%"
                        name={field.name}
                        placeholder={t('form.location_options.placeholder')}
                        testId="community-outreach-form-input-location"
                        value={item || ''}
                        variant={Variants.PRIMARY}
                      />
                    );
                  }}
                />
              </FormField>
            </StyledFormFieldsContainer>
          </StyledFormSectionContainer>
        </CardGrid>
        <CardGrid css={cardGridStyles} direction={Direction.ROW} title={t('contact_details')}>
          <StyledFormSectionContainer>
            <StyledFormFieldsContainer>
              <FormField css={leftFormFieldStyles}>
                <Controller
                  control={control}
                  name="emailAddress"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!(errors.emailAddress == null)}
                      errorDescription={errors.emailAddress?.message as string}
                      fullWidth
                      id={field.name}
                      label={t('form.email_address')}
                      name={field.name}
                      placeholder={t('form.email_address_example')}
                      testId="community-outreach-form-input-email-address"
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
              <FormField css={rightFormFieldStyles}>
                <Controller
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <Input
                      {...field}
                      css={inputNumberStyles}
                      error={!(errors.phoneNumber == null)}
                      errorDescription={errors.phoneNumber?.message as string}
                      fullWidth
                      id={field.name}
                      label={t('form.phone_number')}
                      name={field.name}
                      placeholder={t('form.phone_number_example')}
                      testId="community-outreach-form-input-phone-number"
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setValue('phoneNumber', maskPhoneNumber(event.target.value));
                      }}
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
            </StyledFormFieldsContainer>
          </StyledFormSectionContainer>
        </CardGrid>
        <Checkbox
          checked={consentCheck}
          id="consent-check"
          label={t('form.consent_message')}
          labelstyles={consentLabelStyles}
          name="consentCheck"
          onChange={() => {
            setConsentCheck(!consentCheck);
          }}
        />
        <Button
          loading={isLoading}
          css={submitButtonStyles}
          data-testid="community-outreach-form-submit-button"
          disabled={!(isValid && consentCheck && !isLoading)}
          size={Sizes.LARGE}
          type="submit"
        >
          {t('form.submit_information')}
        </Button>
      </Form>
      {isBlocked && (
        <LeavingPageModal
          confirmText={t('navigate_away_confirm_modal.confirm')}
          declineText={t('navigate_away_confirm_modal.decline')}
          description={t('navigate_away_confirm_modal.description')}
          isShowing={isBlocked}
          onCancel={() => blocker.reset?.()}
          onConfirm={() => blocker.proceed?.()}
          title={t('navigate_away_confirm_modal.title')}
        />
      )}
    </>
  );
};

export default CommunityOutreachForm;
